import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import {
	ColumnFields,
	CustomerDTO,
	Quote,
	RestService,
	DIFFERENCE_FROM_NOW,
	Stop,
	GET_FORMATTED_DATE,
	GET_DATE_FROM_STRING,
} from 'cargo-chief-commons';
import { SharedService } from '@service/shared/shared.service';
import { APP_ENV } from '@domain/constants/app-variables';
import { environment } from '@environment/environment';
import { UtilService } from '@service/util/util.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Subscription } from 'rxjs';

export interface StopDisplay {
	sequence: number;
	typeLabel: string;
	location: string;
	dateWindow: string;
}

@Component({
	selector: 'app-load-detail',
	templateUrl: './load-detail.component.html',
	styleUrls: ['./load-detail.component.scss'],
	providers: [CurrencyPipe],
	encapsulation: ViewEncapsulation.None,
})
export class LoadDetailComponent implements OnInit, OnChanges, OnDestroy {
	@Input() quote: Quote = null;
	@Input() isEditQuote: boolean = false;
	@Input() customer: CustomerDTO = null;
	@Input() canCancel: boolean = false;
	@Output() quoteUpdated = new EventEmitter<Quote>();
	@Output() cancelLoadClicked = new EventEmitter<void>();
	detailsView: Array<Array<ColumnFields>> = [];
	rerunRequestSent: boolean = false;
	customerName: string | null = null;

	LOAD_URL = null;
	SHOW_LOAD_URL = false;
	isLoading = false;
	DIFFERENCE_FROM_NOW = DIFFERENCE_FROM_NOW;

	stopsDisplay: StopDisplay[] = [];

	private customerSubscription: Subscription | null = null;

	constructor(
		public utilService: UtilService,
		public accountSettings: AccountSettingsService,
		private sharedService: SharedService,
		private urlService: UrlService,
		private restService: RestService
	) {}

	ngOnInit() {
		if (
			!this.utilService.isNullOrUndefined(localStorage.getItem('loadURL')) &&
			environment.environment != APP_ENV.PROD
		) {
			this.LOAD_URL = localStorage.getItem('loadURL');

			if (!this.utilService.isNullOrUndefined(this.LOAD_URL) && this.LOAD_URL != 'null') {
				this.SHOW_LOAD_URL = true;
			}
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		// Only react to quote changes
		if (changes['quote']) {
			if (this.utilService.isNullOrUndefined(this.quote)) {
				// Clear customerName and stops when quote becomes null
				this.customerName = null;
				this.stopsDisplay = [];
				return;
			}

			this.buildView();
			this.fetchCustomerName();
		}
	}

	ngOnDestroy() {
		// Clean up subscription to prevent memory leaks
		if (this.customerSubscription) {
			this.customerSubscription.unsubscribe();
		}
	}

	private fetchCustomerName() {
		// Cancel any pending request to prevent race conditions
		if (this.customerSubscription) {
			this.customerSubscription.unsubscribe();
		}

		// Use @Input customer if available and matches the quote's customerCode
		if (this.customer?.name && this.customer?.customerCode === this.quote?.customerCode) {
			this.customerName = this.customer.name;
			return;
		}

		if (
			this.utilService.isNullOrUndefined(this.quote?.customerCode) ||
			this.quote.customerCode === ''
		) {
			this.customerName = null;
			return;
		}

		const currentCustomerCode = this.quote.customerCode;
		const url = this.urlService.getCustomerByCustomerCode(currentCustomerCode);

		this.customerSubscription = this.restService.GET(url).subscribe({
			next: (customer: CustomerDTO) => {
				// Guard against stale responses - only update if customerCode still matches
				if (this.quote?.customerCode === currentCustomerCode) {
					this.customerName = customer?.name || this.quote.customerCode;
				}
			},
			error: () => {
				// Guard against stale responses
				if (this.quote?.customerCode === currentCustomerCode) {
					this.customerName = this.quote.customerCode;
				}
			},
		});
	}

	buildView() {
		this.detailsView = [];
		const details = this.sharedService.getQuoteDetailsView(this.quote);

		// Add Predicted Price to pair with Order Type in the right column
		// QuoteViewTemplate has 11 items, so adding Predicted Price makes it 12 (even count for proper pairing)
		if (this.accountSettings.SHOW_LOADS_PREDICTED_PRICE && this.quote?.predictedPrice && details.length === 11) {
			const predictedPriceField: ColumnFields = {
				icon: 'attach_money',
				labelText: 'Predicted Price',
				labelValue: this.quote.predictedPrice,
				valueType: 'currency',
				valueUnit: '',
				rowClass: 'padding-0-5 font-size-16',
				labelTextClass: 'textWithIcon',
				labelValueClass: 'label-text-light',
				iconClass: 'primary-light-color',
				labelValueFlexLayout: 'end center',
			};
			details.push(predictedPriceField);
		}

		this.detailsView = details
			.map((x, i) => i % 2 == 0 && details.slice(i, i + 2))
			.filter((x) => x);

		this.buildStopsDisplay();
	}

	buildStopsDisplay() {
		const stops: Stop[] = this.quote?.stops || [];

		this.stopsDisplay = stops.map((stop, index) => ({
			sequence: stop.sequenceNumber ?? index + 1,
			typeLabel: this.formatStopType(stop.stopType),
			location: this.formatStopLocation(stop),
			dateWindow: this.formatStopWindow(stop),
		}));
	}

	private formatStopType(stopType: string): string {
		const type = (stopType || '').toUpperCase();

		if (type.includes('PICK')) {
			return 'Pickup';
		}

		if (type.includes('DROP') || type.includes('DELIV')) {
			return 'Dropoff';
		}

		return stopType || '-';
	}

	private formatStopLocation(stop: Stop): string {
		const parts = [stop?.city, stop?.state].filter(
			(part) => !this.utilService.isNullOrUndefined(part) && part !== ''
		);

		return parts.length ? parts.join(', ') : '-';
	}

	private formatStopWindow(stop: Stop): string {
		const start = this.formatStopDate(stop?.startDateFormatted, stop?.startDate, stop?.windowStart);
		const end = this.formatStopDate(stop?.endDateFormatted, stop?.endDate, stop?.windowEnd);

		if (start && end && start !== end) {
			return `${start} - ${end}`;
		}

		return start || end || '-';
	}

	private formatStopDate(formatted: string, parsedDate: any, rawWindow: string): string {
		if (!this.utilService.isNullOrUndefined(formatted) && formatted !== '') {
			return formatted;
		}

		if (!this.utilService.isNullOrUndefined(parsedDate)) {
			return GET_FORMATTED_DATE(parsedDate);
		}

		if (!this.utilService.isNullOrUndefined(rawWindow) && rawWindow !== '') {
			// Raw window values use the TMS format (e.g. 20250602080000) which
			// must be parsed before formatting.
			const parsedRaw = GET_DATE_FROM_STRING(String(rawWindow));

			return parsedRaw ? GET_FORMATTED_DATE(parsedRaw) : '';
		}

		return '';
	}

	rerunLoad() {
		this.isLoading = true;
		const url = this.urlService.rerunBookingAssistant(this.quote.loadId);
		this.restService.POST(url, {}).subscribe({
			next: () => {
				this.utilService.showSnackbar(
					'This load will be re run now',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.rerunRequestSent = true;
			},
			complete: () => {
				this.isLoading = false;
			},
		});
	}
}
